import {Component, signal, computed, ChangeDetectionStrategy} from "@angular/core"
import {NgTemplateOutlet} from "@angular/common"
import {MatMenuModule} from "@angular/material/menu"
import {StringInputComponent} from "@app/common/components/inputs/string-input/string-input.component"
import {ListItemComponent} from "@app/common/components/item/list-item/list-item.component"
import {
    UVControlPoint,
    getControlPointPartNumber,
    isControlPointPart,
    ControlPointPart,
    isTemplateNode,
    SceneNodes,
    getTemplateNodeForObjectPath,
    ObjectPath,
} from "@cm/template-nodes"
import {
    MattressCurve,
    MattresCurveUVChannel,
    MattressUVControlPoint,
    MattressReferenceFrame,
    isMattressReferenceFrameLikeNode,
    createCurveControlPointsForHorizontalLoop,
    createMattressCurveControlPointsForLoopOption,
    findClosestParentComponentGraph,
    collectMattressNodes,
} from "@cm/mattress-nodes"
import type {MattressReferenceFrameLikeNode, CreateMattressCurveForLoopOption} from "@cm/mattress-nodes"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent, SelectionPossibilities} from "@template-editor/components/value-slot/value-slot.component"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {ToggleComponent} from "@app/common/components/buttons/toggle/toggle.component"
import {BaseCurveInspectorComponent} from "@template-editor/components/inspectors/base-curve-inspector/base-curve-inspector.component"
import {Vector3, Vector2} from "@cm/math"
import {NumericInputComponent} from "@app/common/components/inputs/numeric-input/numeric-input.component"
import {InputContainerComponent} from "@app/common/components/inputs/input-container/input-container.component"
import {SceneNodePartClickEvent} from "@app/template-editor/services/scene-manager.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-mattress-curve-inspector",
    templateUrl: "./mattress-curve-inspector.component.html",
    styleUrl: "./mattress-curve-inspector.component.scss",
    imports: [
        MatMenuModule,
        StringInputComponent,
        ListItemComponent,
        InspectorSectionComponent,
        ValueSlotComponent,
        ButtonComponent,
        ToggleComponent,
        InputContainerComponent,
        NumericInputComponent,
        NgTemplateOutlet,
    ],
    standalone: true,
})
export class MattressCurveInspectorComponent extends BaseCurveInspectorComponent<MattressCurve> {
    readonly $showTools = signal(false)

    readonly $allControlPointsIndices = computed(() => {
        return this.$controlPoints().map((_, index) => index)
    })

    readonly $selectedControlPointsIndices = computed(() => {
        const node = this.$node()
        const controlPointCount = this.$controlPoints().length
        const selectedPointsSet = new Set(
            this.sceneManagerService
                .$selectedObjectPathParts()
                .filter(
                    (objectPathPart): objectPathPart is {path: ObjectPath; part: ControlPointPart} =>
                        isControlPointPart(objectPathPart.part) && getTemplateNodeForObjectPath(objectPathPart.path) === node,
                )
                .map((objectPathPart) => getControlPointPartNumber(objectPathPart.part))
                .filter((index) => index < controlPointCount),
        )

        return [...selectedPointsSet]
    })

    override getMeshCurveControlForNode(node: MattressCurve) {
        const meshCurveControl = node
            .getParentMattressNode()
            .getSceneNodePartsForParameterNodePart({templateNode: node, part: "root"}, this.sceneManagerService)
            .map((x) => x.sceneNode)
            .find(SceneNodes.MeshCurveControl.is)

        if (!meshCurveControl) throw new Error("MeshCurveControl not found")
        return meshCurveControl
    }

    override addPointImpl(node: MattressCurve) {
        const canNotAdd = {canAdd: false as const}

        if (!node) return canNotAdd

        const meshCurveControl = this.getMeshCurveControlForNode(node)
        if (!meshCurveControl) return canNotAdd

        const segmentedMeshSceneNode = node.getParentMattressNode().getSegmentedMeshSceneNode(this.sceneManagerService)
        if (!segmentedMeshSceneNode) return canNotAdd

        const baseMeshSceneNode = node.getParentMattressNode().getBaseMeshSceneNode(this.sceneManagerService)
        if (!baseMeshSceneNode) return canNotAdd

        return {
            canAdd: true,
            getNewControlPointsWithInsertionPosition: (sceneNodePartClickEvent: SceneNodePartClickEvent) => {
                const meshIntersection = sceneNodePartClickEvent.target.find(({sceneNodePart}) => sceneNodePart.sceneNode.id === segmentedMeshSceneNode.id)

                if (!meshIntersection) return

                const {intersection} = meshIntersection
                if (!intersection) return

                const {point} = intersection
                if (point === undefined) throw new Error("Point not found")

                const meshQueryData = this.meshGeometryService.projectPointToMeshInPositionSpace(
                    baseMeshSceneNode.completeMeshData.reified,
                    new Vector3(point.x, point.y, point.z),
                )
                if (!meshQueryData) return
                const uv1 = meshQueryData.uvs[MattresCurveUVChannel]
                if (!uv1) throw new Error("UV1 not found")

                const inverseTransform = meshCurveControl.transform.inverse()
                const surfacePoint = Vector3.fromArray(inverseTransform.multiplyVectorXYZW(point.x, point.y, point.z, 1))

                const curveIntersection = sceneNodePartClickEvent.target.find(({sceneNodePart}) => sceneNodePart.sceneNode === meshCurveControl)
                const insertionPosition = this.getInsertionPositionForAddedControlPoint(curveIntersection, surfacePoint)

                const controlPoints = [...node.parameters.controlPoints] as MattressUVControlPoint[]
                const uvSpace = controlPoints.length > 0 ? controlPoints[0].uvSpace : "global"
                const uvCoord: [number, number] = (() => {
                    if (uvSpace === "global") return [uv1.x, uv1.y]
                    const coordVect = uvSpace.fromGlobalUVSpace(uv1, this.sceneManagerService)
                    return [coordVect.x, coordVect.y]
                })()

                if (insertionPosition !== undefined) controlPoints.splice(insertionPosition, 0, {uv: uvCoord, corner: false, sectionBreak: false, uvSpace})
                else controlPoints.push({uv: uvCoord, corner: false, sectionBreak: false, uvSpace})

                return {
                    controlPoints,
                    insertionPosition,
                }
            },
            prefixPath: [node.getParentMattressNode()],
        }
    }

    private meshGeometryService = this.sceneManagerService.getISceneManager().getMeshGeometryQueries()

    addHorizontalLoop() {
        const curve = this.$node()
        if (!curve) return

        const size = (0.8 ** 4 + 0.8 ** 3) / 2
        const controlPoints = createCurveControlPointsForHorizontalLoop(size)

        this.sceneManagerService.modifyTemplateGraph(() => {
            curve.updateParameters({
                controlPoints: controlPoints as typeof curve.parameters.controlPoints,
                closed: true,
            })
        })
    }

    readonly $verticalTranslation = signal<number | undefined>(undefined)

    readonly $loopOptionType = signal<"side" | "top" | "bottom">("side")
    readonly $loopOptionTypeLabel = computed(() => ({side: "Side", top: "Top", bottom: "Bottom"})[this.$loopOptionType()])
    readonly loopTypeOptions: {value: "side" | "top" | "bottom"; label: string}[] = [
        {value: "side", label: "Side"},
        {value: "top", label: "Top"},
        {value: "bottom", label: "Bottom"},
    ]
    readonly $loopSideHeight = signal<number | undefined>(undefined)
    readonly $loopDistanceFromBorder = signal<number | undefined>(undefined)
    readonly $loopControlPointsPerCorner = signal<number | undefined>(3)

    addLoopForOption() {
        const curve = this.$node()
        if (!curve) return

        const mattress = curve.getParentMattressNode()
        if (!mattress) return

        const mattressGeometryParameters = mattress.getMeshGeometryParameters(this.sceneManagerService)
        if (!mattressGeometryParameters) return

        const baseMeshSceneObject = mattress.getBaseMeshSceneNode(this.sceneManagerService)
        if (!baseMeshSceneObject) return
        const reifiedMesh = baseMeshSceneObject.completeMeshData.reified

        const getCanonicalUVForPoint = (point: [number, number, number]): [number, number] => {
            const pointData = this.meshGeometryService.projectPointToMeshInPositionSpace(reifiedMesh, new Vector3(...point))
            if (!pointData) throw new Error("Failed to project point to mesh")
            const uv = pointData.uvs[MattresCurveUVChannel]
            if (!uv) throw new Error("UV not found")
            return [uv.x, uv.y]
        }

        const type = this.$loopOptionType()
        const controlPointsPerCorner = this.$loopControlPointsPerCorner() ?? 3
        let option: CreateMattressCurveForLoopOption
        if (type === "side") {
            const height = this.$loopSideHeight()
            if (height === undefined) return
            option = {type: "side", height, controlPointsPerCorner}
        } else if (type === "top") {
            const distanceFromBorder = this.$loopDistanceFromBorder()
            if (distanceFromBorder === undefined) return
            option = {type: "top", distanceFromBorder, controlPointsPerCorner}
        } else {
            const distanceFromBorder = this.$loopDistanceFromBorder()
            if (distanceFromBorder === undefined) return
            option = {type: "bottom", distanceFromBorder, controlPointsPerCorner}
        }

        const controlPoints = createMattressCurveControlPointsForLoopOption(option, mattressGeometryParameters, getCanonicalUVForPoint)

        this.sceneManagerService.modifyTemplateGraph(() => {
            curve.updateParameters({
                controlPoints: controlPoints as typeof curve.parameters.controlPoints,
                closed: true,
            })
        })
    }
    applyVerticalTranslation() {
        const curve = this.$node()
        if (!curve) return

        const mattress = curve.getParentMattressNode()
        if (!mattress) return
        const mattressGeometryParameters = mattress.getMeshGeometryParameters(this.sceneManagerService)

        if (!mattressGeometryParameters) return

        const baseMeshSceneObject = mattress.getBaseMeshSceneNode(this.sceneManagerService)
        if (!baseMeshSceneObject) return
        const reifiedMesh = baseMeshSceneObject.completeMeshData.reified

        const offset = this.$verticalTranslation()
        if (offset === undefined) return

        const controlPoints = this.$parameters().controlPoints
        if (controlPoints.length === 0) return

        const selectedControlPointsIndices = this.$selectedControlPointsIndices()

        const positions = (() => {
            return (controlPoints as UVControlPoint[]).map((controlPoint) => {
                const pointData = this.meshGeometryService.projectPointToMeshInUVSpace(reifiedMesh, MattresCurveUVChannel, new Vector2(...controlPoint.uv))
                if (!pointData) throw new Error("Failed to find position for uv control point")
                return pointData.position
            })
        })()

        const mattressHeight = mattressGeometryParameters.height

        const maxAllowedOffset =
            offset > 0 ? mattressHeight - Math.max(...positions.map((position) => position.y)) : -Math.min(...positions.map((position) => position.y))

        if ((offset > 0 && offset > maxAllowedOffset) || (offset < 0 && offset < maxAllowedOffset)) return

        const translatedPositions = positions.map((position, idx) =>
            selectedControlPointsIndices.length === 0 || selectedControlPointsIndices.includes(idx) ? position.add(new Vector3(0, offset, 0)) : position,
        )
        const translatedPointData = translatedPositions.map((position) => {
            const pointData = this.meshGeometryService.projectPointToMeshInPositionSpace(reifiedMesh, position)
            if (!pointData) throw new Error("Failed to project point to mesh")
            return pointData
        })

        const translatedControlPoints = (() => {
            return translatedPointData.map((pointData, index) => ({
                uv: pointData.uvs[MattresCurveUVChannel].toArray(),
                corner: controlPoints[index].corner,
                sectionBreak: controlPoints[index].sectionBreak,
                uvSpace: controlPoints[index].uvSpace,
            })) as MattressUVControlPoint[]
        })()

        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({
                controlPoints: translatedControlPoints,
            })
        })
    }

    readonly $controlPointUVSpaceParameterSelectionPossibilities = computed((): SelectionPossibilities<MattressReferenceFrameLikeNode | "global"> => {
        const parentGraph = findClosestParentComponentGraph(this.$node())
        const frames = [...collectMattressNodes(parentGraph, isMattressReferenceFrameLikeNode)]
        return [{name: "Global", value: "global" as const}, ...frames.map((rf) => ({name: rf.parameters.name, value: rf}))]
    })

    controlPointUVSpaceParameterChanged($event: unknown, controlPointIndices: number[]) {
        if (!((isTemplateNode($event) && $event instanceof MattressReferenceFrame) || $event === "global")) return
        const newUVSpace = $event

        const curve = this.$node()
        const controlPoints = controlPointIndices.map((idx) => this.$parameters().controlPoints[idx])
        const globalUVs = controlPoints.map((controlPoint) =>
            curve.toGlobalUVSpace({x: controlPoint.uv[0], y: controlPoint.uv[1]}, controlPoint.uvSpace, this.sceneManagerService),
        )
        const newUVs = globalUVs.map((globalUV) => curve.fromGlobalUVSpace(globalUV, newUVSpace, this.sceneManagerService))

        this.sceneManagerService.modifyTemplateGraph(() => {
            const controlPoints = [...curve.parameters.controlPoints]

            controlPointIndices.forEach((controlPointIndex, i) => {
                controlPoints[controlPointIndex] = {
                    ...controlPoints[controlPointIndex],
                    uv: [newUVs[i].x, newUVs[i].y],
                    uvSpace: newUVSpace,
                }
            })

            curve.updateParameters({
                controlPoints,
            })
        })
    }

    controlPointUVCoordinateChanged($event: unknown, controlPointIndices: number[], coordinateIndex: 0 | 1) {
        if (typeof $event !== "number") return
        const newCoord = $event

        const curve = this.$node()

        this.sceneManagerService.modifyTemplateGraph(() => {
            const controlPoints = [...curve.parameters.controlPoints]

            controlPointIndices.forEach((controlPointIndex) => {
                controlPoints[controlPointIndex] = {
                    ...controlPoints[controlPointIndex],
                    uv: coordinateIndex === 0 ? [newCoord, controlPoints[controlPointIndex].uv[1]] : [controlPoints[controlPointIndex].uv[0], newCoord],
                }
            })

            curve.updateParameters({
                controlPoints,
            })
        })
    }
}
